import { useQuery } from "@tanstack/react-query";
import { legacyAdminGet } from "../utils/PrecargaApi";
import { useAuth } from "./useAuth";

/**
 * Custom hook for managing Precarga Participants
 * @param {object} options - Query options
 * @param {number} options.eventId - Precarga event ID
 * @param {number} options.page - Page number (default 1)
 * @param {number} options.pageSize - Page size (default 20)
 * @param {string} options.search - Search by user email or phone
 * @param {string} options.sortBy - Sort by: email, total_orders, total_spent, card_links_count, last_activity_at, balance
 * @param {string} options.sortDir - Sort direction: asc, desc
 */
export function usePrecargaParticipants({
  eventId,
  page = 1,
  pageSize = 20,
  search = "",
  sortBy = "last_activity_at",
  sortDir = "desc",
} = {}) {
  const { user } = useAuth();
  const token = user?.token;

  // List participants
  const listQuery = useQuery({
    queryKey: ["precargaParticipants", { eventId, page, pageSize, search, sortBy, sortDir }],
    queryFn: async () => {
      const params = { page, pageSize, sortBy, sortDir };
      if (search) params.search = search;

      return await legacyAdminGet(`${eventId}/participants`, params);
    },
    enabled: !!token && !!eventId,
    keepPreviousData: true,
  });

  // Get participant detail
  const getParticipantDetail = async (userId) => {
    if (!eventId || !userId) return null;
    return await legacyAdminGet(`${eventId}/participants/${userId}/detail`);
  };

  return {
    participants: listQuery.data?.items || [],
    totalCount: listQuery.data?.totalCount || 0,
    hasMore: listQuery.data?.hasMore || false,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error,
    refetch: listQuery.refetch,
    getParticipantDetail,
  };
}

export default usePrecargaParticipants;
